import axios from '../utils/AxiosInstance'

/*
conversationContext
the same conversations list is shown in more than one place (the messaging
page and the candidate profile, for instance) and both can be on the screen at
the same time: each one keeps its own list under its own context key
*/
const conversationContext = () => {
  return {
    selectIds: [],
    filters: {},
    paginate: {},
    // totais por filtro de responsável ({ all, mine, unassigned }), vindos
    // do nó facets da busca
    facets: {}
  }
}

export default {
  // namespaced: true,
  state: {
    conversations: {
      conversationContext1: conversationContext(),
      conversationContext2: conversationContext(),
      selectId: false
    }
  },
  mutations: {
    SET_CONVERSATION_SELECT_IDS (store, {contextKey, ids}) {
      store.conversations[contextKey].selectIds = ids
    },
    ADD_CONVERSATION_SELECT_IDS (store, {contextKey, ids}) {
      // prepend new ids to the top of the list (e.g. a newly created contact)
      const existing = store.conversations[contextKey].selectIds.filter((id) => { return !ids.includes(id) })
      store.conversations[contextKey].selectIds = ids.concat(existing)
    },
    APPEND_CONVERSATION_SELECT_IDS (store, {contextKey, ids}) {
      // append ids to the end of the list (e.g. next page of infinite scroll)
      const merged = store.conversations[contextKey].selectIds.slice()
      ids.forEach((id) => {
        if (!merged.includes(id)) {
          merged.push(id)
        }
      })
      store.conversations[contextKey].selectIds = merged
    },
    REMOVE_CONVERSATION_SELECT_IDS (store, {contextKey, ids}) {
      store.conversations[contextKey].selectIds = store.conversations[contextKey].selectIds.filter((v) => { return !ids.includes(v) })
    },
    SET_CONVERSATION_SELECT_ID (store, id) {
      store.conversations.selectId = id
    },
    SET_CONVERSATION_FILTERS (store, {contextKey, filters}) {
      store.conversations[contextKey].filters = filters
    },
    SET_CONVERSATION_PAGINATE (store, {contextKey, paginate}) {
      store.conversations[contextKey].paginate = {
        limit: paginate.limit,
        page: paginate.page,
        pages: paginate.pages,
        total: paginate.total
      }
    },
    SET_CONVERSATION_FACETS (store, {contextKey, facets}) {
      store.conversations[contextKey].facets = facets || {}
    },
    RESET_CONVERSATION_CONTEXT (store, contextKey) {
      store.conversations[contextKey] = conversationContext()
    }
  },
  getters: {
    getConversations: (store, getters) => (contextKey) => {
      return store.conversations[contextKey].selectIds
      .map((id) => { return getters['entities/conversations/find'](id) })
      .filter((conversation) => { return !!conversation })
    },
    getConversationsFilters: (store, getters) => (contextKey) => {
      return store.conversations[contextKey].filters
    },
    getConversationsPaginate: (store, getters) => (contextKey) => {
      return store.conversations[contextKey].paginate
    },
    getConversationsFacets: (store, getters) => (contextKey) => {
      return store.conversations[contextKey].facets
    },
    getConversation: (store, getters) => {
      return getters['entities/conversations/find'](store.conversations.selectId) || null
    },
    getConversationById: (store, getters) => (id) => {
      return getters['entities/conversations/find'](id) || null
    }
  },
  actions: {
    /*
    selectConversations
    loads the first page (or a filtered set) of conversations
    */
    selectConversations ({commit, dispatch, getters, state}, {contextKey, filters}) {
      // the abortid is per context: otherwise one list would abort the other
      return dispatch('getConversations', { ...filters, abortid: `select-conversations-${contextKey}` })
      .then((response) => {
        commit('SET_CONVERSATION_FILTERS', { contextKey, filters })
        commit('SET_CONVERSATION_PAGINATE', { contextKey, paginate: response })
        commit('SET_CONVERSATION_FACETS', { contextKey, facets: response.facets })
        commit('SET_CONVERSATION_SELECT_IDS', { contextKey, ids: response.docs.map((item) => { return item._id }) })
        return response
      })
    },
    /*
    loadMoreConversations
    appends the next page keeping the current list (infinite scroll)
    */
    loadMoreConversations ({commit, dispatch, getters, state}, {contextKey, page}) {
      const filters = { ...getters.getConversationsFilters(contextKey), page: page }
      return dispatch('getConversations', filters)
      .then((response) => {
        commit('SET_CONVERSATION_FILTERS', { contextKey, filters })
        commit('SET_CONVERSATION_PAGINATE', { contextKey, paginate: response })
        commit('SET_CONVERSATION_FACETS', { contextKey, facets: response.facets })
        commit('APPEND_CONVERSATION_SELECT_IDS', { contextKey, ids: response.docs.map((item) => { return item._id }) })
        return response
      })
    },
    /*
    refreshSelectConversations
    silently refreshes the conversations already in the list: fetches them
    without pagination and updates only the ones already present (keeping the
    current order and membership untouched)
    */
    refreshSelectConversations ({commit, dispatch, getters, state}, contextKey) {
      const filters = { ...getters.getConversationsFilters(contextKey) }
      delete filters.page

      return axios
      .get(`/accounts/${getters.getAccount._id}/messaging/conversations`, {
        params: { ...filters, abortid: `refresh-conversations-${contextKey}` }
      })
      .then((response) => {
        // os totais podem ter mudado mesmo sem mexer na lista
        commit('SET_CONVERSATION_FACETS', { contextKey, facets: response.data.facets })

        // keep only the conversations that are already in the list, then upsert them
        const docs = (response.data.docs || []).filter((doc) => {
          return state.conversations[contextKey].selectIds.includes(doc._id)
        })

        if (!docs.length) {
          return []
        }

        return dispatch('entities/conversations/insert', { data: docs })
      })
    },
    /*
    resetConversationsContext
    empties a context list (e.g. when the component that owns it is destroyed)
    */
    resetConversationsContext ({commit}, contextKey) {
      commit('RESET_CONVERSATION_CONTEXT', contextKey)
    },
    /*
    selectConversation
    */
    selectConversation ({commit, dispatch, getters, state}, id) {
      commit('SET_CONVERSATION_SELECT_ID', id)
      return dispatch('getConversation', id)
    },
    /*
    getConversations
    */
    getConversations ({commit, dispatch, getters, state}, filters) {
      return axios
      .get(`/accounts/${getters.getAccount._id}/messaging/conversations`, {params: filters})
      .then((response) => {
        return dispatch('entities/conversations/insert', {
          data: response.data.docs
        })
        .then((result) => {
          return {
            docs: result.conversations || [],
            limit: response.data.limit,
            page: response.data.page,
            pages: response.data.pages,
            total: response.data.total,
            facets: response.data.facets
          }
        })
      })
    },
    /*
    getConversation
    */
    getConversation ({commit, dispatch, getters, state}, id) {
      return axios
      .get(`/accounts/${getters.getAccount._id}/messaging/conversations/${id}`)
      .then((response) => {
        return dispatch('entities/conversations/insert', {
          data: response.data
        })
      })
      .then((result) => {
        return result.conversations[0]
      })
    },
    /*
    createConversation
    the new conversation goes to the top of the list of the context it was
    created from
    */
    createConversation ({commit, dispatch, getters, state}, {contextKey, data}) {
      delete data.$id

      return axios
      .post(`/accounts/${getters.getAccount._id}/messaging/conversations`, data)
      .then((response) => {
        return dispatch('entities/conversations/insert', {
          data: response.data
        })
      })
      .then((result) => {
        if (contextKey) {
          commit('ADD_CONVERSATION_SELECT_IDS', { contextKey, ids: [result.conversations[0]._id] })
        }
        return result.conversations[0]
      })
    },
    /*
    updateConversation
    */
    updateConversation ({commit, dispatch, getters, state}, {id, data}) {
      return axios
      .put(`/accounts/${getters.getAccount._id}/messaging/conversations/${id}`, data)
      .then((response) => {
        return dispatch('entities/conversations/insert', {
          data: response.data
        })
      })
      .then((result) => {
        return result.conversations[0]
      })
    },
    /*
    deleteConversation
    the conversation is gone for every context, not only the one it was
    deleted from
    */
    deleteConversation ({commit, dispatch, getters, state}, id) {
      return axios
      .delete(`/accounts/${getters.getAccount._id}/messaging/conversations/${id}`)
      .then((response) => {
        commit('REMOVE_CONVERSATION_SELECT_IDS', { contextKey: 'conversationContext1', ids: [id] })
        commit('REMOVE_CONVERSATION_SELECT_IDS', { contextKey: 'conversationContext2', ids: [id] })
        dispatch('entities/conversations/delete', id)
        return {}
      })
    },
    /*
    setConversationRead
    flags the conversation as read/unread on the server; returns the updated
    conversation
    */
    setConversationRead ({commit, dispatch, getters, state}, {id, isRead}) {
      return axios
      .put(`/accounts/${getters.getAccount._id}/messaging/conversations/${id}/read`, { is_read: isRead !== false })
      .then((response) => {
        return dispatch('entities/conversations/insert', {
          data: response.data
        })
      })
      .then((result) => {
        return result.conversations[0]
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/messagingConversations.js